// src/pages/products/ProductCreate.js
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Box, 
  Typography, 
  TextField, 
  Button, 
  Paper, 
  Grid, 
  FormControl, 
  InputLabel, 
  Select, 
  MenuItem, 
  Chip,
  FormHelperText,
  Snackbar,
  Alert,
  CircularProgress,
  Divider,
  FormControlLabel,
  Switch,
  FormGroup,
  Card,
  CardMedia,
  IconButton,
  CardActions
} from '@mui/material';
import { 
  Save as SaveIcon, 
  ArrowBack as ArrowBackIcon,
  Delete as DeleteIcon,
  CloudUpload as CloudUploadIcon,
  Add as AddIcon
} from '@mui/icons-material';
import { collection, addDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../../../firebase';
import { uploadToGarage } from '../../../garage';
import { useDropzone } from 'react-dropzone';

const ProductCreate = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [error, setError] = useState('');
  const [uploading, setUploading] = useState(false);
  
  const [formData, setFormData] = useState({
    nombre: '',
    descripción: '',
    precio: '',
    categoría: 'otros',
    imágenes: [],
    stock: 0,
    tallas: [],
    colores: [],
    active: true,
    destacado: false,
    paypalLink: '', // Nuevo campo
    createdAt: null,
    updatedAt: null
  });
  
  const [tallaInput, setTallaInput] = useState('');
  const [colorInput, setColorInput] = useState('');
  const [formErrors, setFormErrors] = useState({});
  
  const { getRootProps, getInputProps } = useDropzone({
    accept: {
      'image/*': ['.jpeg', '.jpg', '.png', '.gif']
    },
    onDrop: acceptedFiles => {
      handleImageUpload(acceptedFiles);
    }
  });
  
  const handleChange = (e) => {
    const { name, value } = e.target;
    
    // Handle numeric values
    if (name === 'precio') {
      // Allow only numeric values and a single decimal point
      const regex = /^$|^[0-9]+(\.[0-9]{0,2})?$/;
      if (regex.test(value)) {
        setFormData({
          ...formData,
          [name]: value
        });
      }
    } else if (name === 'stock') {
      // Allow only integers
      const regex = /^$|^[0-9]+$/;
      if (regex.test(value)) {
        setFormData({
          ...formData,
          [name]: value
        });
      }
    } else {
      // Handle other fields
      setFormData({
        ...formData,
        [name]: value
      });
    }
    
    // Clear error when field is edited
    if (formErrors[name]) {
      setFormErrors({
        ...formErrors,
        [name]: ''
      });
    }
  };
  
  const handleSwitchChange = (e) => {
    const { name, checked } = e.target;
    setFormData({
      ...formData,
      [name]: checked
    });
  };
  
  const handleAddTalla = () => {
    if (!tallaInput.trim()) {
      setFormErrors({
        ...formErrors,
        tallas: 'La talla no puede estar vacía'
      });
      return;
    }
    
    if (!formData.tallas.includes(tallaInput)) {
      setFormData({
        ...formData,
        tallas: [...formData.tallas, tallaInput]
      });
    }
    
    setTallaInput('');
    
    // Clear error
    if (formErrors.tallas) {
      setFormErrors({
        ...formErrors,
        tallas: ''
      });
    }
  };
  
  const handleAddColor = () => {
    if (!colorInput.trim()) {
      setFormErrors({
        ...formErrors,
        colores: 'El color no puede estar vacío'
      });
      return;
    }
    
    if (!formData.colores.includes(colorInput)) {
      setFormData({
        ...formData,
        colores: [...formData.colores, colorInput]
      });
    }
    
    setColorInput('');
    
    // Clear error
    if (formErrors.colores) {
      setFormErrors({
        ...formErrors,
        colores: ''
      });
    }
  };
  
  const handleRemoveTalla = (talla) => {
    setFormData({
      ...formData,
      tallas: formData.tallas.filter(t => t !== talla)
    });
  };
  
  const handleRemoveColor = (color) => {
    setFormData({
      ...formData,
      colores: formData.colores.filter(c => c !== color)
    });
  };
  
  const handleRemoveImage = (index) => {
    const newImages = [...formData.imágenes];
    newImages.splice(index, 1);
    setFormData({
      ...formData,
      imágenes: newImages
    });
  };
  
  const handleImageUpload = async (files) => {
    if (files.length === 0) return;

    setUploading(true);
    setError('');

    try {
      const imageUrls = await Promise.all(
        files.map(file => uploadToGarage(file, 'products'))
      );
      setFormData({ ...formData, imágenes: [...formData.imágenes, ...imageUrls] });
    } catch (error) {
      console.error('Error uploading images:', error);
      setError('Error al subir las imágenes. Inténtalo de nuevo.');
    } finally {
      setUploading(false);
    }
  };
  
  const validateForm = () => {
    const errors = {};
    
    if (!formData.nombre) {
      errors.nombre = 'El nombre es obligatorio';
    }
    
    if (formData.precio === '' || formData.precio === undefined || formData.precio === null) {
      errors.precio = 'El precio es obligatorio';
    }

    if (formData.paypalLink && !formData.paypalLink.startsWith('https://')) {
      errors.paypalLink = 'El enlace de PayPal debe empezar con https://';
    }

    if (!formData.categoría) {
      errors.categoría = 'La categoría es obligatoria';
    }
    
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    setLoading(true);
    setError('');
    
    try {
      // Format data for Firestore
      const productData = {
        ...formData,
        precio: parseFloat(formData.precio),
        stock: parseInt(formData.stock, 10),
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      };
      
      await addDoc(collection(db, 'products'), productData);
      setShowSuccess(true);
      
      // Clear form after a successful submission
      setTimeout(() => {
        navigate('/admin/products');
      }, 2000);
    } catch (error) {
      console.error('Error creating product:', error);
      setError('Error al crear el producto. Inténtalo de nuevo.');
    } finally {
      setLoading(false);
    }
  };
  
  return (
    <Box sx={{ flexGrow: 1 }}>
      <Paper sx={{ p: 3, mb: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography variant="h5">Nuevo Producto</Typography>
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate('/admin/products')}
          >
            Volver
          </Button>
        </Box>
        
        <Divider sx={{ mb: 3 }} />
        
        <form onSubmit={handleSubmit}>
          <Grid container spacing={3}>
            {/* Basic Information */}
            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom>Información Básica</Typography>
            </Grid>
            
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Nombre del producto"
                name="nombre"
                value={formData.nombre}
                onChange={handleChange}
                error={!!formErrors.nombre}
                helperText={formErrors.nombre}
                required
              />
            </Grid>
            
            <Grid item xs={12} md={6}>
              <FormControl fullWidth error={!!formErrors.categoría}>
                <InputLabel>Categoría</InputLabel>
                <Select
                  name="categoría"
                  value={formData.categoría}
                  onChange={handleChange}
                  label="Categoría"
                >
                  <MenuItem value="uniforme">Uniforme</MenuItem>
                  <MenuItem value="material">Material</MenuItem>
                  <MenuItem value="libros">Libros</MenuItem>
                  <MenuItem value="otros">Otros</MenuItem>
                </Select>
                {formErrors.categoría && <FormHelperText>{formErrors.categoría}</FormHelperText>}
              </FormControl>
            </Grid>
            
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Descripción"
                name="descripción"
                value={formData.descripción}
                onChange={handleChange}
                multiline
                rows={3}
              />
            </Grid>
            
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Precio (€)"
                name="precio"
                value={formData.precio}
                onChange={handleChange}
                error={!!formErrors.precio}
                helperText={formErrors.precio}
                InputProps={{
                  startAdornment: <Typography variant="body1" sx={{ mr: 1 }}>€</Typography>,
                }}
                required
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Enlace de pago PayPal"
                name="paypalLink"
                value={formData.paypalLink}
                onChange={handleChange}
                placeholder="https://paypal.me/tuescuela/50.00"
              />
            </Grid>
            
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Stock"
                name="stock"
                value={formData.stock}
                onChange={handleChange}
                InputProps={{
                  inputProps: { min: 0 }
                }}
              />
            </Grid>
            
            <Grid item xs={12}>
              <FormGroup row>
                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.active}
                      onChange={handleSwitchChange}
                      name="active"
                    />
                  }
                  label="Producto activo"
                />
                
                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.destacado}
                      onChange={handleSwitchChange}
                      name="destacado"
                    />
                  }
                  label="Producto destacado"
                />
              </FormGroup>
            </Grid>
            
            <Grid item xs={12}>
              <Divider sx={{ my: 2 }} />
            </Grid>
            
            {/* Tallas y Colores */}
            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom>Tallas y Colores</Typography>
            </Grid>
            
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle1" gutterBottom>
                Tallas Disponibles
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <TextField
                  label="Añadir talla"
                  value={tallaInput}
                  onChange={(e) => setTallaInput(e.target.value)}
                  error={!!formErrors.tallas}
                  helperText={formErrors.tallas}
                  sx={{ mr: 1, flexGrow: 1 }}
                />
                <Button variant="outlined" onClick={handleAddTalla} startIcon={<AddIcon />}>
                  Añadir
                </Button>
              </Box>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                {formData.tallas.length === 0 ? (
                  <Typography variant="body2" color="textSecondary">
                    No hay tallas añadidas
                  </Typography>
                ) : (
                  formData.tallas.map((talla, index) => (
                    <Chip
                      key={index}
                      label={talla}
                      onDelete={() => handleRemoveTalla(talla)}
                    />
                  ))
                )}
              </Box>
            </Grid>
            
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle1" gutterBottom>
                Colores Disponibles
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <TextField
                  label="Añadir color"
                  value={colorInput}
                  onChange={(e) => setColorInput(e.target.value)}
                  error={!!formErrors.colores}
                  helperText={formErrors.colores}
                  sx={{ mr: 1, flexGrow: 1 }}
                />
                <Button variant="outlined" onClick={handleAddColor} startIcon={<AddIcon />}>
                  Añadir
                </Button>
              </Box>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                {formData.colores.length === 0 ? (
                  <Typography variant="body2" color="textSecondary">
                    No hay colores añadidos
                  </Typography>
                ) : (
                  formData.colores.map((color, index) => (
                    <Chip
                      key={index}
                      label={color}
                      onDelete={() => handleRemoveColor(color)}
                    />
                  ))
                )}
              </Box>
            </Grid>
            
            <Grid item xs={12}>
              <Divider sx={{ my: 2 }} />
            </Grid>
            
            {/* Images */}
            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom>Imágenes del Producto</Typography>
              
              <Box
                {...getRootProps()}
                sx={{
                  border: '2px dashed #cccccc',
                  borderRadius: 2,
                  p: 3,
                  textAlign: 'center',
                  cursor: 'pointer',
                  mb: 3,
                  bgcolor: 'background.paper',
                  '&:hover': {
                    bgcolor: 'action.hover',
                  }
                }}
              >
                <input {...getInputProps()} />
                {uploading ? (
                  <CircularProgress size={24} />
                ) : (
                  <>
                    <CloudUploadIcon sx={{ fontSize: 40, color: 'text.secondary', mb: 1 }} />
                    <Typography>
                      Arrastra imágenes aquí, o haz clic para seleccionar archivos
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                      (Solo archivos de imagen)
                    </Typography>
                  </>
                )}
              </Box>
              
              <Grid container spacing={2}>
                {formData.imágenes.map((imageUrl, index) => (
                  <Grid item xs={6} sm={4} md={3} key={index}>
                    <Card>
                      <CardMedia
                        component="img"
                        height="140"
                        image={imageUrl}
                        alt={`Imagen del producto ${index + 1}`}
                      />
                      <CardActions>
                        <IconButton 
                          size="small" 
                          color="error" 
                          onClick={() => handleRemoveImage(index)}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </CardActions>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            </Grid>
            
            <Grid item xs={12}>
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mt: 2 }}>
                <Button
                  variant="outlined"
                  onClick={() => navigate('/admin/products')}
                  disabled={loading}
                >
                  Cancelar
                </Button>
                <Button
                  type="submit"
                  variant="contained"
                  startIcon={loading ? <CircularProgress size={20} /> : <SaveIcon />}
                  disabled={loading}
                >
                  Guardar
                </Button>
              </Box>
            </Grid>
          </Grid>
        </form>
      </Paper>
      
      <Snackbar
        open={showSuccess}
        autoHideDuration={4000}
        onClose={() => setShowSuccess(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity="success">
          Producto creado exitosamente
        </Alert>
      </Snackbar>
      
      <Snackbar
        open={!!error}
        autoHideDuration={4000}
        onClose={() => setError('')}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity="error">
          {error}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ProductCreate;